import React, { useState } from 'react';
import { Globe, Code, Copy, Check, ExternalLink, Phone, Mail, MapPin, MessageCircle, Key, RefreshCw, Shield, Eye, EyeOff, Palette } from 'lucide-react';
import { SettingsSection, SettingsToggle } from '../index';
import api from '../../../services/api';

const TabVitrine = ({ settings, onChange }) => {
  const [copied, setCopied] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [showKey, setShowKey] = useState(false);

  const updateSetting = (key, value) => {
    onChange({ ...settings, [key]: value });
  };

  const apiBase = process.env.REACT_APP_API_URL
    ? process.env.REACT_APP_API_URL.replace('/api', '')
    : window.location.origin;

  const slug = settings?.dealer_slug || '';
  const apiKey = settings?.public_api_key || '';
  const isActive = settings?.vitrine_enabled && slug;

  const widgetBg = settings?.widget_bg_color || '';
  const widgetText = settings?.widget_text_color || '';
  const widgetAccent = settings?.widget_accent_color || '';

  const extraAttrs = [
    widgetAccent ? ` data-accent="${widgetAccent}"` : '',
    widgetBg ? ` data-bg="${widgetBg}"` : '',
    widgetText ? ` data-text="${widgetText}"` : '',
  ].join('');

  const widgetCode = `<!-- Carmin Widget - Intégrez vos véhicules sur votre site -->
<div id="carmin-widget" data-dealer="${slug || 'VOTRE-SLUG'}" data-key="${apiKey || 'VOTRE-CLE-API'}"${extraAttrs}></div>
<script src="${apiBase}/static/carmin-widget.js"></script>`;

  const apiUrl = `${apiBase}/api/public/${slug || 'VOTRE-SLUG'}/vehicles`;

  const handleCopy = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    }
  };

  const handleRegenerateKey = async () => {
    if (!window.confirm('Régénérer la clé API ? L\'ancienne clé ne fonctionnera plus. Vous devrez mettre à jour le code sur votre site.')) return;
    setRegenerating(true);
    try {
      const response = await api.post('/settings/regenerate-api-key');
      onChange(response.data);
    } catch (err) {
      console.error('Failed to regenerate API key:', err);
    } finally {
      setRegenerating(false);
    }
  };

  const slugValid = /^[a-z0-9-]+$/.test(slug) && slug.length >= 3;

  const maskedKey = apiKey
    ? `${apiKey.substring(0, 10)}${'•'.repeat(20)}${apiKey.substring(apiKey.length - 6)}`
    : '';

  return (
    <div className="space-y-6">
      {/* Enable/Disable */}
      <SettingsSection title="Vitrine publique" icon={Globe}>
        <div className="space-y-4">
          <SettingsToggle
            label="Activer la vitrine publique"
            description="Exposez votre stock via une API publique et un widget intégrable sur votre site"
            checked={settings?.vitrine_enabled || false}
            onChange={(v) => updateSetting('vitrine_enabled', v)}
            color="blue"
          />

          {/* Slug */}
          <div>
            <label className="block text-sm font-medium text-label mb-2">
              Identifiant URL (slug)
            </label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted whitespace-nowrap">
                .../public/
              </span>
              <input
                type="text"
                value={slug}
                onChange={(e) => {
                  const val = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                  updateSetting('dealer_slug', val);
                }}
                placeholder="mon-garage"
                className={`flex-1 bg-dark-input border rounded-md px-3 py-2 text-sm text-primary placeholder-muted focus:outline-none focus:ring-2 focus:ring-carmin ${
                  slug && !slugValid ? 'border-red-500/50' : 'border-dark-border'
                }`}
                maxLength={50}
              />
            </div>
            {slug && !slugValid && (
              <p className="mt-1 text-xs text-red-400">
                Minimum 3 caractères, uniquement lettres minuscules, chiffres et tirets
              </p>
            )}
            {slug && slugValid && (
              <p className="mt-1 text-xs text-green-400">
                URL publique : {apiBase}/api/public/{slug}/vehicles
              </p>
            )}
          </div>

          {/* Stock page URL */}
          <div>
            <label className="block text-sm font-medium text-label mb-2">
              <ExternalLink className="h-4 w-4 inline mr-1" />
              URL de votre page stock
            </label>
            <input
              type="url"
              value={settings?.vitrine_url || ''}
              onChange={(e) => updateSetting('vitrine_url', e.target.value)}
              placeholder="https://www.mon-garage.be/stock"
              className="w-full bg-dark-input border border-dark-border text-primary placeholder-muted rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-carmin"
            />
            <p className="mt-1 text-xs text-muted">
              Lien vers la page de votre site web qui affiche vos véhicules (avec le widget intégré)
            </p>
          </div>

          {/* Status indicator */}
          {isActive && slugValid && (
            <div className="flex items-center gap-2 p-3 bg-green-900/20 border border-green-500/30 rounded-lg">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-sm text-green-400 font-medium">
                Vitrine active
              </span>
              {apiKey && (
                <a
                  href={`${apiBase}/api/public/${slug}/vehicles?api_key=${apiKey}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-auto text-sm text-green-400 hover:text-green-300 flex items-center gap-1"
                >
                  Tester l'API <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          )}
        </div>
      </SettingsSection>

      {/* API Key */}
      {isActive && slugValid && (
        <SettingsSection title="Clé API" icon={Key}>
          <div className="space-y-4">
            <p className="text-sm text-muted">
              Cette clé sécurise l'accès à votre API publique. Elle est nécessaire pour
              le widget et les appels API. Ne la partagez qu'avec les personnes autorisées.
            </p>

            {apiKey ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 bg-dark-hover p-3 rounded-lg">
                  <Shield className="h-4 w-4 text-secondary flex-shrink-0" />
                  <code className="text-xs text-label flex-1 break-all font-mono">
                    {showKey ? apiKey : maskedKey}
                  </code>
                  <button
                    onClick={() => setShowKey(!showKey)}
                    className="p-1.5 text-secondary hover:text-label"
                    title={showKey ? 'Masquer' : 'Afficher'}
                  >
                    {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={() => handleCopy(apiKey, 'key')}
                    className="p-1.5 text-secondary hover:text-label"
                    title="Copier la clé"
                  >
                    {copied === 'key' ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>

                <button
                  onClick={handleRegenerateKey}
                  disabled={regenerating}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm text-orange-400 border border-orange-500/30 rounded-md hover:bg-orange-900/20 disabled:opacity-50"
                >
                  <RefreshCw className={`h-4 w-4 ${regenerating ? 'animate-spin' : ''}`} />
                  Régénérer la clé
                </button>

                <div className="p-3 bg-amber-900/20 border border-amber-500/30 rounded-lg">
                  <p className="text-xs text-amber-400">
                    <strong>Attention :</strong> Si vous régénérez la clé, l'ancienne sera immédiatement
                    invalidée. Pensez à mettre à jour le code sur votre site.
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-3 bg-carmin-50 border border-carmin/30 rounded-lg">
                <p className="text-sm text-carmin-light">
                  La clé API sera générée automatiquement lors de l'enregistrement des paramètres.
                  Cliquez sur "Enregistrer" pour activer la vitrine.
                </p>
              </div>
            )}
          </div>
        </SettingsSection>
      )}

      {/* Contact info for public pages */}
      <SettingsSection title="Coordonnées publiques" icon={Phone}>
        <p className="text-sm text-muted mb-4">
          Ces informations seront visibles par les visiteurs via le widget et l'API.
        </p>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-label mb-2">
              <Mail className="h-4 w-4 inline mr-1" />
              Email de contact
            </label>
            <input
              type="email"
              value={settings?.company_email || ''}
              onChange={(e) => updateSetting('company_email', e.target.value)}
              placeholder="contact@mon-garage.be"
              className="w-full bg-dark-input border border-dark-border text-primary placeholder-muted rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-carmin"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-label mb-2">
              <MessageCircle className="h-4 w-4 inline mr-1" />
              WhatsApp
            </label>
            <input
              type="tel"
              value={settings?.company_whatsapp || ''}
              onChange={(e) => updateSetting('company_whatsapp', e.target.value)}
              placeholder="+32 470 12 34 56"
              className="w-full bg-dark-input border border-dark-border text-primary placeholder-muted rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-carmin"
            />
            <p className="mt-1 text-xs text-muted">
              Format international avec indicatif pays
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-label mb-2">
              <MapPin className="h-4 w-4 inline mr-1" />
              Adresse
            </label>
            <input
              type="text"
              value={settings?.company_address || ''}
              onChange={(e) => updateSetting('company_address', e.target.value)}
              placeholder="Rue de l'Industrie 42, 1000 Bruxelles"
              className="w-full bg-dark-input border border-dark-border text-primary placeholder-muted rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-carmin"
            />
          </div>
        </div>
      </SettingsSection>

      {/* Widget Colors */}
      {isActive && slugValid && apiKey && (
        <SettingsSection title="Apparence du widget" icon={Palette}>
          <div className="space-y-4">
            <p className="text-sm text-muted">
              Personnalisez les couleurs de votre widget pour qu'il s'intègre parfaitement
              au design de votre site web. Les changements s'appliquent après enregistrement.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Accent / Highlight color */}
              <div>
                <label className="block text-sm font-medium text-label mb-2">
                  Couleur principale
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={widgetAccent || '#3b82f6'}
                    onChange={(e) => updateSetting('widget_accent_color', e.target.value)}
                    className="w-10 h-10 rounded-lg border border-dark-border cursor-pointer p-0.5"
                  />
                  <div className="flex-1">
                    <input
                      type="text"
                      value={widgetAccent}
                      onChange={(e) => updateSetting('widget_accent_color', e.target.value)}
                      placeholder="#3b82f6"
                      className="w-full bg-dark-input border border-dark-border text-primary placeholder-muted rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-carmin"
                      maxLength={7}
                    />
                  </div>
                </div>
                <p className="mt-1 text-xs text-muted">Boutons, prix, badges</p>
              </div>

              {/* Background color */}
              <div>
                <label className="block text-sm font-medium text-label mb-2">
                  Fond
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={widgetBg || '#ffffff'}
                    onChange={(e) => updateSetting('widget_bg_color', e.target.value)}
                    className="w-10 h-10 rounded-lg border border-dark-border cursor-pointer p-0.5"
                  />
                  <div className="flex-1">
                    <input
                      type="text"
                      value={widgetBg}
                      onChange={(e) => updateSetting('widget_bg_color', e.target.value)}
                      placeholder="#ffffff"
                      className="w-full bg-dark-input border border-dark-border text-primary placeholder-muted rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-carmin"
                      maxLength={7}
                    />
                  </div>
                </div>
                <p className="mt-1 text-xs text-muted">Arrière-plan du widget</p>
              </div>

              {/* Text color */}
              <div>
                <label className="block text-sm font-medium text-label mb-2">
                  Texte
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={widgetText || '#111827'}
                    onChange={(e) => updateSetting('widget_text_color', e.target.value)}
                    className="w-10 h-10 rounded-lg border border-dark-border cursor-pointer p-0.5"
                  />
                  <div className="flex-1">
                    <input
                      type="text"
                      value={widgetText}
                      onChange={(e) => updateSetting('widget_text_color', e.target.value)}
                      placeholder="#111827"
                      className="w-full bg-dark-input border border-dark-border text-primary placeholder-muted rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-carmin"
                      maxLength={7}
                    />
                  </div>
                </div>
                <p className="mt-1 text-xs text-muted">Titres et descriptions</p>
              </div>
            </div>

            {/* Live preview */}
            <div
              className="rounded-xl p-4 border"
              style={{
                backgroundColor: widgetBg || '#ffffff',
                borderColor: widgetBg ? 'transparent' : undefined,
              }}
            >
              <p className="text-xs font-medium mb-3" style={{ color: (widgetText || '#111827') + '99' }}>
                Apercu
              </p>
              <div className="flex items-center gap-3">
                <div className="w-16 h-12 rounded-lg bg-gray-200 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold truncate" style={{ color: widgetText || '#111827' }}>
                    Volkswagen Golf
                  </p>
                  <p className="text-xs" style={{ color: (widgetText || '#111827') + '99' }}>
                    2021 - 45 000 km - Diesel
                  </p>
                </div>
                <span
                  className="text-sm font-bold flex-shrink-0"
                  style={{ color: widgetAccent || '#3b82f6' }}
                >
                  18 900 EUR
                </span>
              </div>
              <div className="mt-3 flex gap-2">
                <span
                  className="text-xs font-medium px-3 py-1 rounded-full text-white"
                  style={{ background: widgetAccent || '#3b82f6' }}
                >
                  Voir le véhicule
                </span>
                <span
                  className="text-xs font-medium px-3 py-1 rounded-full border"
                  style={{
                    color: widgetAccent || '#3b82f6',
                    borderColor: widgetAccent || '#3b82f6',
                  }}
                >
                  Intéressé(e)
                </span>
              </div>
            </div>

            {(widgetAccent || widgetBg || widgetText) && (
              <button
                onClick={() => {
                  updateSetting('widget_accent_color', '');
                  updateSetting('widget_bg_color', '');
                  updateSetting('widget_text_color', '');
                }}
                className="text-sm text-muted hover:text-label underline"
              >
                Réinitialiser les couleurs par défaut
              </button>
            )}
          </div>
        </SettingsSection>
      )}

      {/* Widget embed code */}
      {isActive && slugValid && apiKey && (
        <SettingsSection title="Intégration Widget" icon={Code}>
          <div className="space-y-4">
            <p className="text-sm text-muted">
              Copiez ce code et collez-le dans votre site web pour afficher vos véhicules.
              Le widget s'adapte automatiquement au design de votre page.
            </p>

            <div className="relative">
              <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap">
                {widgetCode}
              </pre>
              <button
                onClick={() => handleCopy(widgetCode, 'widget')}
                className="absolute top-2 right-2 p-2 bg-gray-700 hover:bg-gray-600 rounded-md text-white transition-colors"
                title="Copier le code"
              >
                {copied === 'widget' ? (
                  <Check className="h-4 w-4 text-green-400" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
            </div>

            <div className="p-3 bg-carmin-50 border border-carmin/30 rounded-lg">
              <p className="text-xs text-carmin-light">
                Les couleurs choisies ci-dessus sont automatiquement intégrées au code.
                Il suffit de copier-coller ce code sur votre site.
              </p>
            </div>
          </div>
        </SettingsSection>
      )}

      {/* API endpoint */}
      {isActive && slugValid && apiKey && (
        <SettingsSection title="API REST" icon={Code}>
          <div className="space-y-4">
            <p className="text-sm text-muted">
              Pour les développeurs : utilisez l'API REST pour intégrer vos véhicules
              dans n'importe quel site ou application.
            </p>

            <div className="p-3 bg-dark-hover rounded-lg">
              <p className="text-xs font-medium text-muted mb-2">Authentification</p>
              <p className="text-xs text-label">
                Ajoutez le header <code className="bg-dark-surface px-1 rounded">X-API-Key: votre-clé</code> ou
                le paramètre <code className="bg-dark-surface px-1 rounded">?api_key=votre-clé</code> à chaque requête.
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between bg-dark-hover p-3 rounded-lg">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-muted mb-1">Liste des véhicules</p>
                  <code className="text-xs text-label break-all">GET {apiUrl}</code>
                </div>
                <button
                  onClick={() => handleCopy(apiUrl, 'api')}
                  className="ml-2 p-1.5 text-secondary hover:text-label"
                >
                  {copied === 'api' ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </button>
              </div>

              <div className="bg-dark-hover p-3 rounded-lg">
                <p className="text-xs font-medium text-muted mb-1">Détail d'un véhicule</p>
                <code className="text-xs text-label break-all">
                  GET {apiBase}/api/public/{slug}/vehicles/:id
                </code>
              </div>

              <div className="bg-dark-hover p-3 rounded-lg">
                <p className="text-xs font-medium text-muted mb-1">Infos du concessionnaire</p>
                <code className="text-xs text-label break-all">
                  GET {apiBase}/api/public/{slug}/info
                </code>
              </div>

              <div className="bg-dark-hover p-3 rounded-lg">
                <p className="text-xs font-medium text-muted mb-1">Filtres disponibles</p>
                <code className="text-xs text-label break-all">
                  GET {apiBase}/api/public/{slug}/filters
                </code>
              </div>
            </div>

            <div className="p-3 bg-dark-hover rounded-lg">
              <p className="text-xs font-medium text-muted mb-1">Rate limiting</p>
              <p className="text-xs text-label">
                60 requêtes / minute par clé API. En cas de dépassement, vous recevrez une erreur 429.
              </p>
            </div>

            <div className="p-3 bg-amber-900/20 border border-amber-500/30 rounded-lg">
              <h4 className="text-sm font-medium text-amber-300 mb-1">SEO / AEO</h4>
              <p className="text-xs text-amber-400">
                L'API et le widget incluent automatiquement des données structurées
                (JSON-LD / Schema.org) pour le référencement naturel et les moteurs
                de réponse IA. Le backlink "Propulsé par Carmin" contribue à votre
                référencement mutuel.
              </p>
            </div>
          </div>
        </SettingsSection>
      )}
    </div>
  );
};

export default TabVitrine;
